import React, { useEffect, useMemo, useState, useCallback } from 'react';
import EntityTable from '../components/EntityTable';
import Toolbar from '../components/Toolbar';
import RelLink from '../components/RelLink';
import { Users, Auth } from '../api/admin';
import { formatEastern } from '../../utils/formatDate';

// Every account on one screen, one row per user: email, roles, and how many
// listings and orders belong to them — each count jumps to the Listings or
// Orders tab already filtered to that person. This is the hub for "what has
// this user done?"; other tabs link here by email.
//
// Only the Broker role can be toggled. Admin is never offered (and the API
// refuses it) so a hijacked admin session can't create more admins.

// PostHog identifies signed-in users by email (AuthModal calls identify(email)),
// so the person page keyed by email lists that user's session recordings.
const POSTHOG_PROJECT_URL = 'https://us.posthog.com/project/402021';
const recordingsUrl = (email) =>
    `${POSTHOG_PROJECT_URL}/person/${encodeURIComponent(email)}#activeTab=sessionRecordings`;

const UsersTab = ({ filters, goTo }) => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [query, setQuery] = useState(filters.email || '');
    const [busy, setBusy] = useState(null);
    const [notice, setNotice] = useState(null);

    useEffect(() => { setQuery(filters.email || ''); }, [filters.email]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await Users.list();
            setUsers(result.users || []);
        } catch (e) {
            setError(e.message);
            setUsers([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const onQueryChange = (value) => {
        setQuery(value);
        goTo({ tab: 'users', email: value || undefined }, { replace: true });
    };

    const visible = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return users;
        return users.filter((u) => [u.email, u.first_name, u.last_name].filter(Boolean).join(' ').toLowerCase().includes(q));
    }, [users, query]);

    const run = async (key, request, successText) => {
        setBusy(key);
        setNotice(null);
        try {
            await request();
            setNotice({ kind: 'ok', text: successText });
            await load();
        } catch (e) {
            setNotice({ kind: 'err', text: e.message });
        } finally {
            setBusy(null);
        }
    };

    const toggleBroker = (user) => {
        const isBroker = user.roles.includes('Broker');
        const verb = isBroker ? 'Remove the Broker role from' : 'Make';
        if (!window.confirm(`${verb} ${user.email}${isBroker ? '' : ' a broker'}?`)) return;
        return run(`broker:${user.email}`,
            () => (isBroker ? Auth.removeRole(user.email, 'Broker') : Auth.assignRole(user.email, 'Broker')),
            isBroker ? `${user.email} is no longer a broker.` : `${user.email} is now a broker.`);
    };

    const revoke = (user) => {
        if (!window.confirm(`Sign ${user.email} out of every device right now?`)) return;
        return run(`revoke:${user.email}`, () => Auth.revoke(user.email), `All sessions for ${user.email} have been ended.`);
    };

    // A count that links to the tab filtered to this user, or a muted zero.
    const countLink = (n, tab, linkFilters) =>
        n > 0 ? (
            <RelLink tab={tab} filters={linkFilters} goTo={goTo} className="users-count">
                {n}
            </RelLink>
        ) : <span className="users-count users-count-zero">0</span>;

    const columns = [
        {
            key: 'email',
            header: 'Email',
            render: (u) => <span className="users-email">{u.email}</span>,
        },
        {
            key: 'name',
            header: 'Name',
            render: (u) => ([u.first_name, u.last_name].filter(Boolean).join(' ') || <span className="muted">—</span>),
        },
        {
            key: 'roles',
            header: 'Roles',
            render: (u) => (
                <span className="users-roles">
                    {(u.roles || []).filter((r) => r !== 'User').map((r) => (
                        <span key={r} className={`users-role users-role-${r.toLowerCase()}`}>{r}</span>
                    ))}
                    {(u.roles || []).filter((r) => r !== 'User').length === 0 && <span className="muted">User</span>}
                </span>
            ),
        },
        {
            key: 'date_created',
            header: 'Signed Up',
            width: '120px',
            render: (u) => (u.date_created
                ? formatEastern(u.date_created, { year: 'numeric', month: 'short', day: 'numeric' })
                : <span className="muted">—</span>),
        },
        {
            key: 'active_listings',
            header: 'Active Listings',
            width: '130px',
            render: (u) => countLink(u.active_listings, 'listings', { sellerEmail: u.email }),
        },
        {
            key: 'orders_bought',
            header: 'Orders Bought',
            width: '130px',
            render: (u) => countLink(u.orders_bought, 'orders', { buyerEmail: u.email }),
        },
        {
            key: 'orders_sold',
            header: 'Orders Sold',
            width: '120px',
            render: (u) => countLink(u.orders_sold, 'orders', { sellerEmail: u.email }),
        },
    ];

    const actions = (u) => {
        const isBroker = u.roles.includes('Broker');
        const rowBusy = busy !== null && busy.endsWith(`:${u.email}`);
        return (
            <div className="users-actions">
                {isBroker && (
                    <RelLink tab="brokers" filters={{ brokerEmail: u.email }} goTo={goTo} title="Their listings with cert images">
                        Broker View
                    </RelLink>
                )}
                <a
                    href={recordingsUrl(u.email)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="external-link"
                    title="Open this user's PostHog session recordings"
                >
                    Recordings
                </a>
                <button
                    type="button"
                    className="btn-ghost btn-sm"
                    disabled={busy !== null}
                    onClick={() => toggleBroker(u)}
                >
                    {rowBusy && busy.startsWith('broker:') ? 'Saving…' : isBroker ? 'Remove Broker' : 'Make Broker'}
                </button>
                <button
                    type="button"
                    className="btn-ghost btn-sm users-revoke"
                    disabled={busy !== null}
                    onClick={() => revoke(u)}
                    title="End every session this user has"
                >
                    {rowBusy && busy.startsWith('revoke:') ? 'Signing Out…' : 'Sign Out Everywhere'}
                </button>
            </div>
        );
    };

    return (
        <div className="admin-tab-panel">
            <Toolbar>
                <input
                    placeholder="Search by email"
                    value={query}
                    onChange={(e) => onQueryChange(e.target.value)}
                />
                {query && (
                    <button className="btn-ghost" onClick={() => onQueryChange('')}>Clear</button>
                )}
                <span className="users-stat">
                    {visible.length} of {users.length} user{users.length === 1 ? '' : 's'}
                </span>
            </Toolbar>

            {notice && <p className={notice.kind === 'err' ? 'error-message' : 'mapping-success'}>{notice.text}</p>}
            {loading && <p className="loading">Loading…</p>}
            {error && <p className="error-message">Error: {error}</p>}

            {!loading && !error && (
                <EntityTable
                    columns={columns}
                    rows={visible}
                    rowKey="email"
                    actions={actions}
                    emptyMessage={query ? `No user matches "${query}".` : 'No users yet.'}
                />
            )}
        </div>
    );
};

export default UsersTab;
